import Phaser from 'phaser';
import type { SaveManager } from '@/game/systems/SaveManager';
import type { AudioManager } from '@/game/systems/AudioManager';
import type { SongBackground } from '@/types';
import { BACKGROUNDS, backgroundTextureKey } from '@/data/backgrounds';
import { ensureMotifTexture } from '@/game/objects/motifIcons';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, animateCoinSpend, bodyTextStyle, createButton, createCoinPanel, titleTextStyle } from '@/game/ui/theme';

// Portrait aspect (matches the source art) so thumbnails show the whole illustration
// undistorted rather than a stretched or cropped slice.
const SWATCH_W = 108;
const SWATCH_H = 192;

/** Lets the player pick a single global background that overrides every song's own
 * default (or "Auto" to keep each song's default). Mirrors CatSelectScene's
 * unlock/purchase/select pattern, just with one grid instead of two per-lane rows. */
export class BackgroundSelectScene extends Phaser.Scene {
  private saveManager!: SaveManager;
  private audioManager!: AudioManager;
  private coinText!: Phaser.GameObjects.Text;

  constructor() {
    super('BackgroundSelectScene');
  }

  create(): void {
    this.saveManager = this.game.registry.get('saveManager');
    this.audioManager = this.game.registry.get('audioManager');

    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, PALETTE.bgBottom).setOrigin(0);
    this.add.text(INTERNAL_WIDTH / 2, 60, 'Choose a Background', titleTextStyle(32)).setOrigin(0.5);

    const backBtn = createButton(this, 90, 60, 120, 56, 'Back', PALETTE.panelLight, 20);
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));

    this.coinText = createCoinPanel(this, INTERNAL_WIDTH - 110, 44, this.saveManager.getData().coins);

    const cols = 4;
    const spacingX = 150;
    const spacingY = 228;
    const startX = INTERNAL_WIDTH / 2 - ((cols - 1) * spacingX) / 2;
    const startY = 210;

    const selectedId = this.saveManager.getSelectedBackgroundId();

    // "Auto" tile keeps each song's own default background instead of a fixed one.
    this.buildTile(startX, startY, null, selectedId === null);

    BACKGROUNDS.forEach((def, i) => {
      const index = i + 1;
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * spacingX;
      const y = startY + row * spacingY;
      this.buildTile(x, y, def.id, selectedId === def.id, def.name, def.price, this.saveManager.isBackgroundUnlocked(def.id), def);
    });
  }

  private buildTile(
    x: number,
    y: number,
    id: SongBackground | null,
    selected: boolean,
    name = 'Auto',
    price = 0,
    unlocked = true,
    def?: (typeof BACKGROUNDS)[number]
  ): void {
    const radius = 12;
    const bounds = new Phaser.Geom.Rectangle(x - SWATCH_W / 2, y - SWATCH_H / 2, SWATCH_W, SWATCH_H);

    if (def) {
      const image = this.add.image(x, y, backgroundTextureKey(def.id));
      image.setDisplaySize(SWATCH_W, SWATCH_H);
      const maskShape = this.make.graphics({});
      maskShape.fillRoundedRect(bounds.x, bounds.y, SWATCH_W, SWATCH_H, radius);
      image.setMask(maskShape.createGeometryMask());

      if (def.motif !== 'none') {
        const key = ensureMotifTexture(this, def.motif, def.motifColor, def.motifColor2);
        this.add.image(x, y - SWATCH_H / 2 + 26, key).setDisplaySize(32, 32).setAlpha(0.95);
      }
    } else {
      const bg = this.add.graphics();
      bg.fillGradientStyle(PALETTE.panel, PALETTE.panel, PALETTE.panelLight, PALETTE.panelLight, 1);
      bg.fillRoundedRect(bounds.x, bounds.y, SWATCH_W, SWATCH_H, radius);
      this.add.text(x, y, '🔀', bodyTextStyle(34)).setOrigin(0.5);
    }

    const border = this.add.graphics();
    border.lineStyle(selected ? 4 : 2, selected ? PALETTE.accent : 0xffffff, selected ? 1 : 0.25);
    border.strokeRoundedRect(bounds.x, bounds.y, SWATCH_W, SWATCH_H, radius);

    if (!unlocked) {
      this.add.rectangle(x, y, SWATCH_W, SWATCH_H, 0x000000, 0.5);
      this.add.text(x, y, '🔒', bodyTextStyle(28)).setOrigin(0.5);
    }

    this.add.text(x, y + SWATCH_H / 2 + 16, name, bodyTextStyle(14)).setOrigin(0.5);
    if (!unlocked) {
      this.add.text(x, y + SWATCH_H / 2 + 33, `${price} coins`, bodyTextStyle(12, '#ffe066')).setOrigin(0.5);
    }

    const hitZone = this.add.zone(x, y, SWATCH_W, SWATCH_H + 40).setInteractive({ useHandCursor: true });
    hitZone.on('pointerup', () => this.onTileTapped(id, price, unlocked));
  }

  private onTileTapped(id: SongBackground | null, price: number, unlocked: boolean): void {
    if (id === null) {
      this.saveManager.setSelectedBackground(null);
      this.scene.restart();
      return;
    }
    if (!unlocked) {
      const before = this.saveManager.getData().coins;
      if (this.saveManager.spendCoins(price)) {
        this.saveManager.unlockBackground(id);
        this.audioManager.playUnlock();
        animateCoinSpend(this, this.coinText, before, this.saveManager.getData().coins, () => this.scene.restart());
      }
      return;
    }
    this.saveManager.setSelectedBackground(id);
    this.scene.restart();
  }
}
